import { useTickets } from '@services/tickets/queries/useTickets'
import type { TicketStatus } from '@services/tickets/types'
import { Wrench } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  Badge,
  CardLink,
  CardSkeleton,
  DashboardCard,
  EmptyState,
  Row,
  useFormatDate
} from './shared'

const STATUS_TONE: Record<TicketStatus, 'teal' | 'amber' | 'gray' | 'red'> = {
  NEW: 'teal',
  IN_PROGRESS: 'amber',
  RESOLVED: 'teal',
  CLOSED: 'gray'
}

export function TicketsCard() {
  const { t } = useTranslation('tickets')
  const { data, isLoading } = useTickets()
  const formatDate = useFormatDate()

  const tickets = (data?.results ?? []).slice(0, 3)

  return (
    <DashboardCard
      icon={Wrench}
      title={t('card.title')}
      action={<CardLink to="/user/tickets">{t('card.all')}</CardLink>}
    >
      {isLoading ? (
        <CardSkeleton />
      ) : tickets.length === 0 ? (
        <EmptyState
          text={t('card.empty')}
          action={<CardLink to="/user/tickets">{t('card.create')}</CardLink>}
        />
      ) : (
        <div className="divide-y divide-ink/[0.04]">
          {tickets.map((ticket) => (
            <Row
              key={ticket.id}
              label={
                <span className="line-clamp-1">
                  {ticket.title}
                  <span className="ml-2 text-xs text-muted-foreground">
                    {formatDate(ticket.created_at)}
                  </span>
                </span>
              }
            >
              <Badge tone={STATUS_TONE[ticket.status] ?? 'gray'}>
                {t(`statuses.${ticket.status}`)}
              </Badge>
            </Row>
          ))}
        </div>
      )}
    </DashboardCard>
  )
}
